import { useEffect, useRef } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { ToastViewport } from "../components/ToastViewport";
import { Brand } from "../components/Brand";
import { AccountMenu, BottomNav, RouteSweep } from "../components/nav";
import { LiveRegion } from "../components/LiveRegion";
import { MockBanner } from "../components/MockBanner";
import { MiniPlayer } from "../components/player";
import { SignInGate } from "../pages/SignInGate";
import { MotionRoot } from "../motion/MotionRoot";
import { isReduced } from "../motion/preference";
import { announce } from "../state/announcer";
import { requestTitleFocus } from "./focus";
import { useServices, useSession, useSync } from "./services";

/**
 * 画面の骨組み：開発用モックの帯・本文（いちばん上は広場の絵）・固定プレイヤー・下のメニュー・読み上げの場所。
 * 固定しない上部バーにブランド・使い方・アカウントをまとめる
 * - 画面を移ったら、新しい画面の見出しへフォーカスを移す（読み上げで「どこに来たか」が分かる）
 * - 移ったときは、いちばん上を3色の細い帯が横切り、見出しの文字が跳ねて出る（参照元のページ移動の帯を、画面をふさがない細い帯にした）
 */
export function AppShell() {
  const session = useSession();
  const { sync, saved } = useServices();
  const location = useLocation();
  // 前に見ていた画面（StrictMode で効果が2回走っても、同じ画面なら何もしない）
  const prevPath = useRef(location.pathname);

  // ログインできたら、再生の同期を始める
  const signedIn = session.status === "signed_in";
  const publicGuide = location.pathname === "/guide";
  const standalonePost = /^\/posts\/[^/]+\/?$/.test(location.pathname);
  useEffect(() => {
    if (!signedIn && !publicGuide) document.title = "ほろりばレコード";
  }, [signedIn, publicGuide]);
  useEffect(() => {
    if (!signedIn) return;
    sync.start();
    void saved.load();
    return () => {
      sync.stop();
      saved.clear();
    };
  }, [signedIn, sync, saved]);

  useConnectionAnnouncements();

  useEffect(() => {
    // 同じ画面の中の移動（#main への「本文へ移動」など）は、画面の移動として扱わない
    const here = location.pathname;
    if (prevPath.current === here) return;
    prevPath.current = here;
    const root = document.documentElement;
    if (!isReduced()) {
      root.classList.remove("route-arrive");
      // 一度外してから付け直すと、続けて移っても動きがやり直される
      void root.offsetWidth;
      root.classList.add("route-arrive");
    }
    const t = window.setTimeout(() => root.classList.remove("route-arrive"), 1200);
    if (!location.hash) {
      window.scrollTo({ top: 0 });
      requestTitleFocus();
    }
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <>
      <a className="skip-link" href="#main">
        本文へ移動
      </a>
      <MockBanner />
      <RouteSweep />
      <main id="main" className={`main${signedIn && !standalonePost ? " main--with-player" : ""}`}>
        <div className="app-topbar">
          <Brand />
          <div className="app-topbar__actions">
            <Link to="/guide" className="app-help">使い方</Link>
            {signedIn && <AccountMenu />}
          </div>
        </div>
        {signedIn || publicGuide ? <Outlet /> : <SignInGate />}
      </main>
      {signedIn && !standalonePost && <MiniPlayer />}
      {signedIn && !standalonePost && <BottomNav />}
      {signedIn && <ToastViewport />}
      <LiveRegion />
      <MotionRoot />
    </>
  );
}

/** 更新停止中になった・戻ったことを読み上げる */
function useConnectionAnnouncements() {
  const sync = useSync();
  const prev = useRef(sync.conn);
  useEffect(() => {
    const before = prev.current;
    prev.current = sync.conn;
    if (before === "live" && sync.conn === "stale") {
      announce("更新停止中になりました。再生の表示は止めています。つながり直すのを待っています。", "assertive", "connection");
    } else if ((before === "stale" || before === "reconnecting") && sync.conn === "live") {
      announce("つながり直しました。再生の状態とキューを最新にしました。", "polite", "connection");
    }
  }, [sync.conn]);
}
